import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Turn a relative /api/media/... URL into an absolute one the browser can load.
export const mediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
};

/**
 * What went wrong, in words a customer can act on.
 *
 * FastAPI rejects a request in three different shapes and only two of them
 * were ever read. A `detail` string is ours. A `detail` object carries a
 * `message`. But a body that fails validation before the handler runs comes
 * back as a LIST of field errors, and every caller treated that as
 * unreadable and fell back to something generic.
 *
 * That matters most on the checkout. `alex@gmail` satisfies the browser's
 * own type=email check, so the field looks fine and nothing is flagged, and
 * Python's validator then refuses it. The buyer is told "Checkout failed.
 * Please try again." on the one screen where we are asking to be paid, and
 * trying again does exactly the same thing.
 */
const FIELD_LABELS = {
  email: "email address",
  agency_name: "brokerage name",
  contact_name: "your name",
  phone: "phone number",
  // The field is still called `postcode` in the model — renaming a stored
  // field is a migration, and this file's job is only to say it out loud in
  // the language of the person reading it.
  postcode: "ZIP code",
  state: "state",
  price: "price",
  amount: "amount",
};

export function errorMessage(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return detail.message || fallback;
  }
  if (Array.isArray(detail) && detail.length) {
    // Name the field. "Invalid" on a form with six boxes on it is not an
    // instruction, and the reason Pydantic gives ("the part after the
    // @-sign is a special-use or reserved name") is not written for the
    // person reading it.
    const named = detail
      .slice(0, 2)
      .map((e) => {
        const field = [...(e?.loc || [])]
          .reverse()
          .find((part) => typeof part === "string" && part !== "body");
        const label = FIELD_LABELS[field] || (field || "").replace(/_/g, " ");
        if (!label) return "";
        return /email/.test(field || "")
          ? `That ${label} doesn't look right. Check it and try again.`
          : `Please check the ${label} and try again.`;
      })
      .filter(Boolean);
    if (named.length) return named.join(" ");
  }
  return fallback;
}

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("wb_admin_token");
  config.headers = config.headers || {};
  // A request that brought its own credentials keeps them. The client
  // portal sends its session as a Bearer token, and overwriting it with the
  // CRM's meant an agent signed into the CRM could never open the portal
  // link they had just sent a client: 401, session wiped, back to sign-in.
  const own = typeof config.headers.get === "function"
    ? config.headers.get("Authorization") : config.headers.Authorization;
  if (token && !own) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
